import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import Keycloak from 'keycloak-js';

/**
 * Tenant context for axios interceptors.
 * This is set by the TenantContext provider.
 */
let currentTenantId: string | null = null;
let currentWorkspaceId: string | null = null;

/**
 * Set the current tenant context for axios interceptors.
 * Called by TenantContext when tenant changes.
 */
export const setTenantContext = (tenantId: string | null, workspaceId?: string | null): void => {
  currentTenantId = tenantId;
  currentWorkspaceId = workspaceId ?? null;
};

/**
 * Get the current tenant context.
 */
export const getTenantContext = (): { tenantId: string | null; workspaceId: string | null } => ({
  tenantId: currentTenantId,
  workspaceId: currentWorkspaceId,
});

/**
 * Setup axios interceptors to attach Keycloak token and tenant headers to all requests
 */
export const setupAxiosInterceptors = (keycloak: Keycloak | null): void => {
  // Request interceptor to add token and tenant headers
  axios.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      if (keycloak?.token) {
        // Ensure token is fresh (update if expiring in next 30 seconds)
        try {
          await keycloak.updateToken(30);
        } catch (error) {
          console.error('Failed to refresh token', error);
          keycloak.login();
          return Promise.reject(error);
        }

        // Merge with existing headers instead of replacing
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${keycloak.token}`;
      }
      
      // Add tenant headers if available
      if (currentTenantId) {
        config.headers = config.headers || {};
        config.headers['X-Tenant-Id'] = currentTenantId;
      }
      if (currentWorkspaceId) {
        config.headers = config.headers || {};
        config.headers['X-Workspace-Id'] = currentWorkspaceId;
      }
      
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor to handle 401 errors
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401 && keycloak) {
        keycloak.login();
      }
      return Promise.reject(error);
    }
  );
};

/**
 * Create a custom axios instance with Keycloak authentication and tenant headers
 */
export const createAuthenticatedAxios = (keycloak: Keycloak | null): AxiosInstance => {
  const instance = axios.create();

  instance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      if (keycloak?.token) {
        try {
          await keycloak.updateToken(30);
        } catch (error) {
          console.error('Failed to refresh token', error);
          keycloak.login();
          return Promise.reject(error);
        }

        // Merge with existing headers instead of replacing
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${keycloak.token}`;
      }
      
      // Add tenant headers if available
      if (currentTenantId) {
        config.headers = config.headers || {};
        config.headers['X-Tenant-Id'] = currentTenantId;
      }
      if (currentWorkspaceId) {
        config.headers = config.headers || {};
        config.headers['X-Workspace-Id'] = currentWorkspaceId;
      }
      
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401 && keycloak) {
        keycloak.login();
      }
      return Promise.reject(error);
    }
  );

  return instance;
};
